import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../utils/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch business info from backend
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const apiUrl = getApiUrl();
        const response = await fetch(`${apiUrl}/api/business-info`);
        const data = await response.json();
        setBusinessInfo(data);
      } catch (error) {
        console.error('Error fetching business info:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBusinessInfo();
  }, []);

  // Static location data (hours are not stored in business-info)
  const locations = [
    {
      name: 'Baldwin',
      address: 'Baldwin, NY',
      hours: 'Wednesday - Saturday: 11:00 AM - 10:00 PM',
    },
    {
      name: 'Home Studio Islandia',
      address: 'Islandia, NY',
      hours: 'Monday & Tuesday: 5:00 PM - 10:00 PM',
    },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast.success('Thank you for contacting us! We will get back to you soon.');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  if (loading) {
    return (
      <div className="contact-page" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#b8860b', fontStyle: 'italic', fontSize: '1.2rem' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="marble-overlay"></div>
        <div className="container">
          <h1 className="hero-medium">Get In Touch</h1>
          <p className="body-large">We'd love to hear from you</p>
        </div>
      </section>

      <section className="contact-content section-padding">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <h2 className="heading-2">Send Us a Message</h2>
              <div className="gold-divider"></div>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="form-textarea"
                  ></textarea>
                </div>
                <button type="submit" className="btn-primary">
                  Send Message
                  <Send size={20} className="btn-icon" />
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="contact-info-wrapper">
              <h2 className="heading-2">Contact Information</h2>
              <div className="gold-divider"></div>
              
              <div className="contact-info-section">
                <div className="contact-info-item">
                  <Phone size={24} className="contact-icon" />
                  <div>
                    <h4 className="info-label">Phone</h4>
                    <a href={`tel:${businessInfo?.phone}`} className="info-value">
                      {businessInfo?.phone || '(631) 772-9261'}
                    </a>
                  </div>
                </div>

                <div className="contact-info-item">
                  <Mail size={24} className="contact-icon" />
                  <div>
                    <h4 className="info-label">Email</h4>
                    <a href={`mailto:${businessInfo?.email}`} className="info-value">
                      {businessInfo?.email || 'Apolonia@glamouroverall.com'}
                    </a>
                  </div>
                </div>
              </div>

              <div className="locations-info">
                <h3 className="heading-3">Our Locations</h3>
                {locations.map((location, index) => (
                  <div key={index} className="location-card">
                    <div className="location-header">
                      <MapPin size={20} className="location-icon" />
                      <h4 className="location-name">{location.name}</h4>
                    </div>
                    <p className="location-address">{location.address}</p>
                    <div className="location-hours">
                      <Clock size={16} className="hours-icon" />
                      <span>{location.hours}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
